import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import SportsRugbyIcon from '@mui/icons-material/SportsRugby';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

import { fetchCompetitionResults } from './ResultsApi';

const getTeamPhoto = (team) => {
  if (team?.logoUrl) return team.logoUrl;
  return '';
};

const buildTeamUrl = (team) => {
  if (!team?._id) return null;
  const teamLabel = team?.name || team?.fullName || team?._id;
  return `/dsas-view/equipe/${encodeURIComponent(teamLabel)}_${team._id}`;
};

const formatDateLabel = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parsed);
};

const formatTimeLabel = (value) => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const hasValidScore = (match) =>
  typeof match?.homeScore === 'number' &&
  typeof match?.awayScore === 'number';

const getWinnerFlags = (match) => {
  if (!hasValidScore(match)) {
    return { homeWon: false, awayWon: false };
  }

  return {
    homeWon: match.homeScore > match.awayScore,
    awayWon: match.awayScore > match.homeScore,
  };
};

const parsePositiveDay = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

const scoreBoxSx = {
  minWidth: { xs: 30, md: 40 },
  height: { xs: 32, md: 40 },
  px: 0.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #d6d6d6',
  backgroundColor: '#fff',
  fontWeight: 600,
  fontSize: { xs: 18, md: 26 },
  lineHeight: 1,
};

const renderCenterContent = (match) => {
  const hasScore = hasValidScore(match);

  if (!hasScore) {
    return (
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: { xs: 18, md: 24 },
          lineHeight: 1,
          color: '#1f1f1f',
          whiteSpace: 'nowrap',
        }}
      >
        {formatTimeLabel(match.dateTime || match.time)}
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="center"
    >
      <Box sx={scoreBoxSx}>{match.homeScore}</Box>
      <Box sx={scoreBoxSx}>{match.awayScore}</Box>
    </Stack>
  );
};

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

export default function ResultsSection({
  provider,
  discipline,
  competitionIds = [],
  season,
  day = null,
  onNavigate,
  onMetaChange,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    items: [],
    selectedDay: null,
    nbDays: null,
  });

  const competitionKey = useMemo(
    () => competitionIds?.join(',') || '',
    [competitionIds]
  );

    useEffect(() => {
    let ignore = false;

    const load = async () => {
        try {
        setLoading(true);
        setError('');

        const result = await fetchCompetitionResults({
            provider,
            discipline,
            competitionIds,
            season,
            day,
        });

        if (!ignore) {
            const nextData = {
                items: Array.isArray(result?.items) ? result.items : [],
                selectedDay: normalizeDayValue(result?.selectedDay),
                nbDays: parsePositiveDay(result?.nbDays),
                };

            setData((prev) => {
            const sameItems =
                JSON.stringify(prev.items) === JSON.stringify(nextData.items);

            if (
                sameItems &&
                prev.selectedDay === nextData.selectedDay &&
                prev.nbDays === nextData.nbDays
            ) {
                return prev;
            }

            return nextData;
            });

            onMetaChange?.({
            selectedDay: nextData.selectedDay,
            nbDays: nextData.nbDays,
            });
        }
        } catch (e) {
        if (!ignore) {
            setError('Impossible de charger les résultats.');
            setData({
            items: [],
            selectedDay: null,
            nbDays: null,
            });

            onMetaChange?.({
            selectedDay: null,
            nbDays: null,
            });
        }
        } finally {
        if (!ignore) {
            setLoading(false);
        }
        }
    };

    load();

    return () => {
        ignore = true;
    };
    }, [provider, discipline, competitionKey, season, day]);

  const handleLinkClick = (event, url) => {
    if (!url || !onNavigate) return;
    event.preventDefault();
    onNavigate(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data.items.length) {
    return (
      <Alert severity="info">
        Aucun résultat disponible pour cette sélection.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {data.items.map((group, groupIndex) => (
        <Box key={group.date || groupIndex}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: '#555',
              mb: 0.75,
              textTransform: 'capitalize',
            }}
          >
            {formatDateLabel(group.date)}
          </Typography>

          <Stack spacing={1}>
            {(group.matches || []).map((match, matchIndex) => {
              const homeUrl = buildTeamUrl(match.homeTeam);
              const awayUrl = buildTeamUrl(match.awayTeam);
              const { homeWon, awayWon } = getWinnerFlags(match);

              return (
                <Card
                  key={match._id || `${groupIndex}-${matchIndex}`}
                  variant="outlined"
                  sx={{
                    borderRadius: 0.5,
                    borderColor: '#d9d9d9',
                    backgroundColor: '#fafafa',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 1, md: 1.5 },
                      '&:last-child': { pb: { xs: 1, md: 1.5 } },
                    }}
                  >
                    <Stack spacing={0.75}>
                      <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                            xs: '1fr 96px 1fr',
                            md: '1fr 140px 1fr',
                            },
                            alignItems: 'center',
                            columnGap: { xs: 0, md: 2 },
                        }}
                        >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: 'auto 0fr', md: '32px minmax(0, 1fr)' },
                                alignItems: 'center',
                                columnGap: 1,
                                minWidth: 0,
                            }}
                            >
                            <Avatar
                                src={getTeamPhoto(match.homeTeam)}
                                alt={match.homeTeam?.name || 'home'}
                                sx={{
                                width: { xs: 24, md: 30 },
                                height: { xs: 24, md: 30 },
                                bgcolor: 'grey.200',
                                flexShrink: 0,
                                order: { xs: 2, md: 1 },
                                justifySelf: { xs: 'end', md: 'start' },
                                }}
                            >
                                {discipline === 'soccer' ? (
                                    <SportsSoccerIcon fontSize="small" />
                                ) : (
                                    <SportsRugbyIcon fontSize="small" />
                                )}
                            </Avatar>

                            <Typography
                                sx={{
                                fontSize: { xs: 13, md: 16 },
                                fontWeight: homeWon ? 700 : 500,
                                textAlign: { xs: 'right', md: 'right' },
                                minWidth: 0,
                                lineHeight: 1.15,
                                order: { xs: 1, md: 2 },
                                display: '-webkit-box',
                                WebkitLineClamp: { xs: 2, md: 1 },
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: { xs: 'normal', md: 'nowrap' },
                                textOverflow: { xs: 'clip', md: 'ellipsis' },
                                wordBreak: 'break-word',
                                }}
                            >
                                {homeUrl ? (
                                <Link
                                    href={homeUrl}
                                    underline="hover"
                                    color="inherit"
                                    onClick={(e) => handleLinkClick(e, homeUrl)}
                                >
                                    {match.homeTeam?.name || '-'}
                                </Link>
                                ) : (
                                match.homeTeam?.name || '-'
                                )}
                            </Typography>
                            </Box>

                        <Box
                            sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minWidth: 0,
                            }}
                        >
                            {renderCenterContent(match)}
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '0fr auto', md: 'minmax(0, 1fr) 32px' },
                                alignItems: 'center',
                                columnGap: 1,
                                minWidth: 0,
                            }}
                            >
                            <Typography
                                sx={{
                                fontSize: { xs: 13, md: 16 },
                                fontWeight: awayWon ? 700 : 500,
                                textAlign: { xs: 'left', md: 'left' },
                                minWidth: 0,
                                lineHeight: 1.15,
                                order: { xs: 2, md: 1 },
                                display: '-webkit-box',
                                WebkitLineClamp: { xs: 2, md: 1 },
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: { xs: 'normal', md: 'nowrap' },
                                textOverflow: { xs: 'clip', md: 'ellipsis' },
                                wordBreak: 'break-word',
                                }}
                            >
                                {awayUrl ? (
                                <Link
                                    href={awayUrl}
                                    underline="hover"
                                    color="inherit"
                                    onClick={(e) => handleLinkClick(e, awayUrl)}
                                >
                                    {match.awayTeam?.name || '-'}
                                </Link>
                                ) : (
                                match.awayTeam?.name || '-'
                                )}
                            </Typography>

                            <Avatar
                                src={getTeamPhoto(match.awayTeam)}
                                alt={match.awayTeam?.name || 'away'}
                                sx={{
                                width: { xs: 24, md: 30 },
                                height: { xs: 24, md: 30 },
                                bgcolor: 'grey.200',
                                flexShrink: 0,
                                order: { xs: 1, md: 2 },
                                justifySelf: { xs: 'start', md: 'end' },
                                }}
                            >
                                {discipline === 'soccer' ? (
                                    <SportsSoccerIcon fontSize="small" />
                                ) : (
                                    <SportsRugbyIcon fontSize="small" />
                                )}
                            </Avatar>
                            </Box>
                        </Box>

                      {match.matchUrl ? (
                        <Box sx={{ textAlign: 'center', pt: 0.25 }}>
                          <Link
                            href={match.matchUrl}
                            underline="hover"
                            color="success.main"
                            onClick={(e) => handleLinkClick(e, match.matchUrl)}
                            sx={{
                              fontSize: 11,
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: 0.2,
                            }}
                          >
                            Suivez le match en live !
                          </Link>
                        </Box>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}